import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';

export interface ScoreFocusedEvent {
  sub_event: { id: string };
  date: string;
  count: number;
}

/** Pre-computed chart series: [timestamp, count][] */
export type EventSeriesData = [number, number][];

/**
 * Fallback score memory in days, used until the mosaic reports the real
 * `rolling_sum_period`. Mirrors the API defaults we never override
 * (`rating_stability` 90 + `benchmark_duration` 7). Authoritative value always
 * comes from the `score_ts_top_events` response.
 */
export const DEFAULT_SCORE_WINDOW_DAYS = 97;

@Injectable({ providedIn: 'root' })
export class ScoreFocusedEventsService {
  private readonly eventsSubject = new BehaviorSubject<
    ScoreFocusedEvent[] | null
  >(null);
  private readonly timeframeSubject = new BehaviorSubject<string | null>(null);

  /** Raw events array (for consumers that need it, e.g. analytics) */
  readonly events$ = this.eventsSubject.asObservable();

  /** Current focused timeframe */
  readonly timeframe$ = this.timeframeSubject.asObservable();

  /**
   * The score's rolling window in days, published by the mosaic once the API
   * reports it. The chart widens its events fetch to this so every driver has
   * activity data, including ones whose news predates the global timeframe.
   * Read synchronously at fetch time — no consumer needs to react to it.
   */
  scoreWindowDays: number | null = null;

  /** Whether events data is available */
  readonly hasEvents$: Observable<boolean> = this.events$.pipe(
    map((events) => events != null && events.length > 0),
    distinctUntilChanged(),
  );

  /**
   * Pre-computed map of eventId → [timestamp, count][] series.
   * Computed once per events update, shared by all micro-chart instances.
   */
  readonly eventSeriesMap$: Observable<Map<string, EventSeriesData>> =
    this.events$.pipe(
      map((events) => this.computeEventSeriesMap(events)),
      distinctUntilChanged(),
    );

  setEvents(events: ScoreFocusedEvent[] | null): void {
    this.eventsSubject.next(events);
  }

  setTimeframe(timeframe: string | null): void {
    this.timeframeSubject.next(timeframe);
  }

  clear(): void {
    this.eventsSubject.next(null);
    this.timeframeSubject.next(null);
    this.scoreWindowDays = null;
  }

  private computeEventSeriesMap(
    events: ScoreFocusedEvent[] | null,
  ): Map<string, EventSeriesData> {
    const result = new Map<string, EventSeriesData>();
    if (!events) return result;

    // Single pass: group by eventId, then by date, summing counts
    const grouped = new Map<string, Map<string, number>>();
    for (const e of events) {
      const eventId = e.sub_event.id;
      let dateMap = grouped.get(eventId);
      if (!dateMap) {
        dateMap = new Map<string, number>();
        grouped.set(eventId, dateMap);
      }
      dateMap.set(e.date, (dateMap.get(e.date) || 0) + e.count);
    }

    // Convert to chart-ready [timestamp, count][] format
    for (const [eventId, dateMap] of grouped) {
      result.set(
        eventId,
        Array.from(dateMap, ([date, count]) => [
          new Date(date + 'T00:00:00Z').getTime(),
          count,
        ]),
      );
    }

    return result;
  }
}
